import { useCallback, useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/Bits";
import api, { COUNTRY_NAMES, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { Send, RefreshCw, Inbox as InboxIcon, Users, Sparkles, Wand2 } from "lucide-react";

export default function Inbox() {
  const [data, setData] = useState({ conversations: [], total_unread: 0 });
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [showInstruction, setShowInstruction] = useState(false);
  const bottom = useRef(null);

  const loadList = useCallback(async () => {
    const { data } = await api.get("/inbox", { params: { include_groups: showGroups } });
    setData(data);
  }, [showGroups]);

  const loadThread = useCallback(async (phone) => {
    const { data } = await api.get(`/inbox/${encodeURIComponent(phone)}`);
    setThread(data);
  }, []);

  useEffect(() => { loadList(); const t = setInterval(loadList, 15000); return () => clearInterval(t); }, [loadList]);
  useEffect(() => { if (active) loadThread(active); }, [active, loadThread]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post(`/inbox/${encodeURIComponent(active)}/reply`, { text: draft });
      setDraft("");
      toast.success("Réponse envoyée");
      loadThread(active);
      loadList();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/inbox/${encodeURIComponent(active)}/suggest`, { instruction });
      setDraft(data.suggestion);
      toast.success(`Proposition générée (contexte : ${data.campaign_name})`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Génération impossible");
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <Layout
      title="Réponses"
      subtitle={`${data.total_unread} message(s) non lu(s)`}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setShowGroups((s) => !s)} data-testid="toggle-groups-btn"
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors duration-200 ${
              showGroups ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-600"}`}>
            <Users className="h-4 w-4" /> Groupes{data.group_messages ? ` (${data.group_messages})` : ""}
          </button>
          <button onClick={() => { loadList(); if (active) loadThread(active); }} data-testid="refresh-inbox-btn"
            className="rounded-lg border border-gray-200 bg-white p-2 hover:-translate-y-[1px] transition-transform duration-200">
            <RefreshCw className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-190px)]" data-testid="inbox-page">
        <div className="card-tactical overflow-y-auto" data-testid="conversation-list">
          {data.conversations.length === 0 && (
            <div className="p-8 text-center">
              <InboxIcon className="h-5 w-5 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Aucune réponse reçue pour le moment.</p>
            </div>
          )}
          {data.conversations.map((c) => (
            <button
              key={c.phone}
              onClick={() => setActive(c.phone)}
              data-testid={`conversation-${c.phone}`}
              className={`w-full border-b border-gray-100 px-4 py-3.5 text-left transition-colors duration-150 ${
                active === c.phone ? "bg-[#E7F7EF]" : "hover:bg-gray-50"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="mono text-sm text-gray-900">{c.phone}</span>
                <div className="flex items-center gap-1.5">
                  {c.needs_human && (
                    <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-medium text-white"
                      data-testid={`handoff-badge-${c.phone}`}>À traiter</span>
                  )}
                  {c.unread > 0 && (
                    <span className="mono rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-medium text-white"
                      data-testid={`unread-badge-${c.phone}`}>{c.unread}</span>
                  )}
                </div>
              </div>
              <p className="mt-0.5 text-xs text-gray-500 truncate">
                {c.is_group ? `Groupe · ${c.group_name || "sans nom"}` : (
                  <>{c.first_name ? `${c.first_name} · ` : ""}{COUNTRY_NAMES[c.country] || c.country || "—"}</>
                )}
              </p>
              <p className="mt-1 text-xs text-gray-600 truncate">
                {c.last_direction === "out" ? "Vous : " : ""}{c.last_text || "—"}
              </p>
              <p className="mono mt-1 text-[10px] text-gray-400">{fmtTime(c.last_at)}</p>
            </button>
          ))}
        </div>

        <div className="card-tactical lg:col-span-2 flex flex-col overflow-hidden" data-testid="conversation-thread">
          {!active ? (
            <div className="flex-1 grid place-items-center p-8">
              <p className="text-sm text-gray-500">Sélectionnez une conversation.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <p className="mono text-sm font-medium text-gray-900">{active}</p>
                  <p className="text-xs text-gray-500">
                    {thread?.contact?.first_name || "Contact inconnu"}
                    {thread?.contact?.country ? ` · ${COUNTRY_NAMES[thread.contact.country] || thread.contact.country}` : ""}
                  </p>
                </div>
                {thread?.contact && <StatusBadge status={thread.contact.status} testId="thread-contact-status" />}
              </div>

              {thread?.alert && (
                <div className="mx-5 mt-4 rounded-lg border border-red-300 bg-red-50 p-3"
                  data-testid="thread-handoff-alert">
                  <p className="text-xs font-medium text-red-900">
                    Intervention humaine requise — {thread.alert.reason}
                  </p>
                  <p className="mt-1 text-[11px] text-red-800">
                    Les réponses automatiques sont suspendues sur cette conversation
                    ({thread.alert.exchanges} échange(s) IA) jusqu'à ce que vous la marquiez traitée.
                  </p>
                  <button onClick={async () => {
                    await api.post(`/alerts/${thread.alert.id}/resolve`);
                    toast.success("Alerte marquée comme traitée");
                    loadList();
                    loadThread(active);
                  }} data-testid="resolve-alert-btn"
                    className="mt-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700">
                    Marquer comme traité
                  </button>
                </div>
              )}

              <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4" data-testid="thread-messages">
                {(thread?.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3.5 py-2.5 ${
                      m.direction === "out" ? "bg-[#E7F7EF] text-gray-900" : "bg-gray-100 text-gray-900"}`}>
                      <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                      <p className="mono mt-1 text-[10px] text-gray-500">
                        {m.ai_generated ? (m.ai_kind === "handoff" ? "IA · transfert · " : "IA · ") : ""}{fmtTime(m.at)}
                        {m.campaign_id ? " · campagne" : ""}
                        {m.delivery_status ? ` · ${m.delivery_status}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
                {thread?.messages?.length === 0 && (
                  <p className="text-sm text-gray-500">Aucun message.</p>
                )}
                <div ref={bottom} />
              </div>

              <div className="border-t border-gray-100 p-4">
                {thread?.contact?.status === "opted_out" && (
                  <p className="mb-2 text-xs text-red-600">
                    Ce contact est désinscrit : ne lui envoyez que des réponses strictement nécessaires.
                  </p>
                )}
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <button onClick={suggest} disabled={suggesting} data-testid="ai-suggest-btn"
                    className="flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-colors duration-200">
                    <Sparkles className={`h-3.5 w-3.5 ${suggesting ? "animate-pulse" : ""}`} />
                    {suggesting ? "Génération…" : "Proposer une réponse IA"}
                  </button>
                  <button onClick={() => setShowInstruction((s) => { if (s) setInstruction(""); return !s; })} data-testid="ai-guide-toggle-btn"
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${
                      showInstruction ? "border-gray-800 bg-gray-900 text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
                    <Wand2 className="h-3.5 w-3.5" /> Guider l'IA
                  </button>
                </div>
                {showInstruction && (
                  <div className="mb-2 flex items-center gap-2" data-testid="ai-instruction-row">
                    <input
                      data-testid="ai-instruction-input"
                      value={instruction}
                      onChange={(e) => setInstruction(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); suggest(); } }}
                      placeholder="Ex. : propose un rendez-vous mardi matin"
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                    <button onClick={suggest} disabled={suggesting} data-testid="ai-generate-with-instruction-btn"
                      className="rounded-lg bg-gray-900 px-3 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-40">
                      Générer
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    data-testid="reply-input"
                    rows={2}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Écrire une réponse…"
                    className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                  <button onClick={send} disabled={sending || !draft.trim()} data-testid="send-reply-btn"
                    className="rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-40 flex items-center gap-2">
                    <Send className="h-4 w-4" /> Envoyer
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
